import { Route } from 'vue-router';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import SeoService from '../../core/services/seo/SeoService';
import {
    setDocumentTitle,
    createCanonicalLink,
} from './metaTags';
import { logger } from '../logger';
import { getConfig } from '@superbet-group/web.lib.config';
import { RouteName } from '@app/router/constants/RouteName';
import { SiteLanguageConfig } from '@modules/app/siteLanguage/siteLanguageConfig';
import { Store } from 'vuex';

const MetaEnum = {
    Name: 1,
    Property: 2,
    HttpEquiv: 3,
} as const;

type MetaType = typeof MetaEnum[keyof typeof MetaEnum];

export type SeoMetaInformation = {
    Key: string;
    Type: MetaType;
    Value: string;
};

export type HtmlTag = {
    Key: string;
    Value: string;
};

interface SeoHtmlInformation {
    Key: string;
    Value: string;
}

export async function seoRouterOnAfterEach(to: Route, _from: Route, store: Store<unknown>) {
    const prerenderReadyStateEnabled = await featureFlagService.getFeatureValue('prerender.ready-state', false);
    if (prerenderReadyStateEnabled) {
        checkPageNotFound(to);
    }

    const enabled = await featureFlagService.getFeatureValue('seo-engine.is-enabled', false);

    if (!enabled) {
        return;
    }

    const url = getSeoEngineUrl(to);
    try {
        const metadata = await SeoService.getInstance().getMetadata(url);

        if (!metadata) {
            store.dispatch('navigation/setRouteSeoData', { url, data: null });
            return;
        }

        const { Title, Canonical, MetaTags, HtmlTags } = metadata;

        const contentTitle = HtmlTags?.find((tag) => tag.Key.trim() === 'contentTitle')?.Value;
        const seoCopy = HtmlTags?.find((tag) => tag.Key.trim() === 'seoCopy')?.Value;
        store.dispatch('navigation/setRouteSeoData', {
            url,
            data: {
                title: contentTitle ?? null,
                seoCopy: seoCopy ?? null,
            },
        });
        Title ? setDocumentTitle(Title) : null;
        Canonical ? createCanonicalLink(Canonical) : null;
        MetaTags?.forEach(setMetaTag);
        HtmlTags?.forEach(setHtmlTag);
    } catch (e) {
        store.dispatch('navigation/setRouteSeoData', {
            url,
            data: {
                title: null,
                seoCopy: null,
            },
        });
        logger.error(e as Error);
    }
}

export function getSeoEngineUrl(to: Route) {
    const localeAsPath = SiteLanguageConfig.hasLocaleInUrls
        ? '/' + ((getConfig().appLocale).locale).toLocaleLowerCase()
        : '';
    return `${window.location.protocol}//${window.location.host}${localeAsPath}${to.path}`;
}

function setMetaTag({ Key, Type, Value }: SeoMetaInformation) {
    let type = getType(Type);

    const metaTag = document.querySelector(`meta[${type}='${Key}']`) as HTMLMetaElement;

    if (metaTag) {
        metaTag.setAttribute('content', Value);
    } else {
        const newMeta = document.createElement('meta');
        newMeta.setAttribute(type, Key);
        newMeta.setAttribute('content', Value);
        document.head.appendChild(newMeta);
    }
}

function setHtmlTag({ Key, Value }: SeoHtmlInformation) {
    /*
        we want to allow SEO executives to update textContent of any html tag.
        also we want to protect us and them from making human mistakes of overwriting wrong tags.
        therefore we are adding `data-seoengine` attribute to the tags we want them to be able to target.
    */
    const targetElement = document.querySelector(`${Key}[data-seoengine]`);
    if (targetElement) {
        targetElement.textContent = Value;
    }
}

function getType(type: MetaType) : string {
    switch (type) {
        case MetaEnum.Name:
            return 'name';
        case MetaEnum.Property:
            return 'property';
        case MetaEnum.HttpEquiv:
            return 'http-equiv';
        default:
            throw 'could not match meta type.';
    }
}

// This will allow crawling bots to see a clean 404 status code. We are checking if page is a Not Found rendering and we add a meta data that forces prerender to output to bots a certain status code.
// Normaly we would push a 404 code but 404 will be interpreted by Cloudfront -> ErrorPages as 200 with redirect to SPA root object so we are
// pushing 416 code and we are adding in CloudFront-> ErrorPages a rule to interpret 416 as 404
function checkPageNotFound(to: Route) {
    if (to.name === RouteName.App.CatchAll || to.name === RouteName.App.NotFound) {
        setMetaTag(
            {
                Key: 'prerender-status-code',
                Type: 1,
                Value: '416',
            },
        );
    } else {
        document.querySelector('meta[name=prerender-status-code]')?.remove();
    }
}
